import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Trophy, Edit3, Plus, Play, Trash2, RotateCcw, UserMinus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BracketPage() {
  const { isAdmin } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMatch, setEditMatch] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newTournament, setNewTournament] = useState({ name: '', team_count: 8 });
  const [scores, setScores] = useState({ team1: '', team2: '', winner: '' });
  const [assignSlot, setAssignSlot] = useState(null); // { matchId, slot }
  const [availableTeams, setAvailableTeams] = useState([]);

  useEffect(() => {
    fetchTournament();
  }, []);

  const fetchTournament = async () => {
    try {
      const { data } = await axios.get(`${API}/tournaments/active`);
      setTournament(data);
    } catch (err) {
      console.error('Error fetching tournament:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTournament = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tournaments`, newTournament);
      toast.success('Tournament created!');
      setCreateOpen(false);
      fetchTournament();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create tournament');
    }
  };

  const handleAssignTeams = async () => {
    if (!tournament) return;
    try {
      await axios.put(`${API}/tournaments/${tournament.id}/assign-teams`, {});
      toast.success('Teams assigned to bracket!');
      fetchTournament();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign teams');
    }
  };

  const handleUpdateMatch = async () => {
    if (!tournament || !editMatch) return;
    try {
      const updateData = {};
      if (scores.team1) updateData.team1_score = parseInt(scores.team1);
      if (scores.team2) updateData.team2_score = parseInt(scores.team2);
      if (scores.winner) updateData.winner_id = scores.winner;
      
      await axios.put(
        `${API}/tournaments/${tournament.id}/matches/${editMatch.id}`,
        updateData
      );
      toast.success('Match updated!');
      setEditMatch(null);
      setScores({ team1: '', team2: '', winner: '' });
      fetchTournament();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update match');
    }
  };

  const handleDeleteTournament = async () => {
    if (!tournament || !window.confirm('Delete this tournament?')) return;
    try {
      await axios.delete(`${API}/tournaments/${tournament.id}`);
      toast.success('Tournament deleted');
      setTournament(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const openEditDialog = (match) => {
    setEditMatch(match);
    setScores({
      team1: match.team1_score?.toString() || '',
      team2: match.team2_score?.toString() || '',
      winner: match.winner_id || ''
    });
  };

  const openAssignSlot = async (matchId, slot) => {
    setAssignSlot({ matchId, slot });
    try {
      const { data } = await axios.get(`${API}/teams`);
      // Filter out teams already assigned in this tournament
      const assignedIds = new Set();
      tournament?.matches?.forEach(m => {
        if (m.team1_id) assignedIds.add(m.team1_id);
        if (m.team2_id) assignedIds.add(m.team2_id);
      });
      setAvailableTeams(data.filter(t => !assignedIds.has(t.id)));
    } catch {
      setAvailableTeams([]);
    }
  };

  const handleAssignTeam = async (teamId) => {
    if (!tournament || !assignSlot) return;
    try {
      await axios.put(`${API}/tournaments/${tournament.id}/matches/${assignSlot.matchId}/assign`, {
        team_id: teamId,
        slot: assignSlot.slot
      });
      toast.success('Team assigned!');
      setAssignSlot(null);
      fetchTournament();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign team');
    }
  };

  const handleResetMatch = async (matchId) => {
    if (!tournament) return;
    try {
      await axios.put(`${API}/tournaments/${tournament.id}/matches/${matchId}/reset`);
      toast.success('Match reset to default');
      setEditMatch(null);
      fetchTournament();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset match');
    }
  };

  const handleRemoveTeam = async (matchId, slot) => {
    if (!tournament) return;
    try {
      await axios.put(`${API}/tournaments/${tournament.id}/matches/${matchId}/remove-team`, { slot });
      toast.success('Team removed from bracket');
      setEditMatch(null);
      fetchTournament();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove team');
    }
  };

  // Group matches by round
  const matchesByRound = tournament?.matches?.reduce((acc, match) => {
    if (!acc[match.round]) acc[match.round] = [];
    acc[match.round].push(match);
    return acc;
  }, {}) || {};

  const rounds = Object.keys(matchesByRound).map(Number).sort((a, b) => a - b);
  const totalRounds = rounds.length;

  const getRoundName = (round, total) => {
    if (round === total) return 'Finals';
    if (round === total - 1) return 'Semi-Finals';
    if (round === total - 2) return 'Quarter-Finals';
    return `Round ${round}`;
  };

  return (
    <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505]" data-testid="bracket-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mb-12">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-2">
              Tournament
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase text-white">
              {tournament?.name || 'Tournament Bracket'}
            </h1>
          </div>

          {isAdmin && (
            <div className="flex gap-3">
              {!tournament && (
                <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                  <Button 
                    onClick={() => setCreateOpen(true)}
                    className="bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none"
                    data-testid="create-tournament-button"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Create Tournament
                  </Button>
                  <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="create-tournament-dialog">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-xl text-white uppercase">
                        Create Tournament
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateTournament} className="space-y-6 mt-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-400 text-sm uppercase tracking-wide">
                          Tournament Name
                        </Label>
                        <Input
                          value={newTournament.name}
                          onChange={(e) => setNewTournament({...newTournament, name: e.target.value})}
                          placeholder="e.g., Season 1 Championship"
                          required
                          className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400"
                          data-testid="tournament-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400 text-sm uppercase tracking-wide">
                          Team Count
                        </Label>
                        <Select
                          value={newTournament.team_count.toString()}
                          onValueChange={(val) => setNewTournament({...newTournament, team_count: parseInt(val)})}
                        >
                          <SelectTrigger className="bg-black border-zinc-800 text-white rounded-none h-12" data-testid="team-count-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#121212] border-zinc-800 rounded-none">
                            <SelectItem value="4" className="text-white">4 Teams</SelectItem>
                            <SelectItem value="8" className="text-white">8 Teams</SelectItem>
                            <SelectItem value="16" className="text-white">16 Teams</SelectItem>
                            <SelectItem value="32" className="text-white">32 Teams</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12"
                        data-testid="submit-tournament-button"
                      >
                        Create Tournament
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}

              {tournament && tournament.status === 'registration' && (
                <Button 
                  onClick={handleAssignTeams}
                  className="bg-green-500 text-black font-bold uppercase tracking-wide hover:bg-green-400 rounded-none"
                  data-testid="assign-teams-button"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Start Tournament
                </Button>
              )}

              {tournament && (
                <Button 
                  onClick={handleDeleteTournament}
                  variant="outline"
                  className="border-red-500/50 text-red-500 hover:bg-red-500/10 rounded-none"
                  data-testid="delete-tournament-button"
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* No Tournament */}
        {!loading && !tournament && (
          <div className="text-center py-24 bg-[#121212] border border-zinc-800" data-testid="no-tournament-message">
            <Trophy className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="font-heading text-xl text-white mb-2">No Active Tournament</h2>
            <p className="text-zinc-500">
              {isAdmin ? 'Create a tournament to get started.' : 'Check back later for upcoming tournaments.'}
            </p>
          </div>
        )}

        {/* Bracket Display */}
        {!loading && tournament && tournament.matches?.length > 0 && (
          <div className="overflow-x-auto pb-8">
            <div className="flex gap-8 min-w-max" data-testid="bracket-container">
              {rounds.map((round) => (
                <div key={round} className="flex flex-col">
                  <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-4 text-center">
                    {getRoundName(round, totalRounds)}
                  </h3>
                  <div 
                    className="flex flex-col justify-around flex-1"
                    style={{ gap: `${Math.pow(2, round - 1) * 32}px` }}
                  >
                    {matchesByRound[round]?.map((match) => (
                      <div
                        key={match.id}
                        className={`relative bg-[#1A1A1A] border w-64 ${
                          match.status === 'completed' 
                            ? 'border-cyan-500/50' 
                            : 'border-zinc-800'
                        } bracket-node`}
                        data-testid={`bracket-node-${match.id}`}
                      >
                        {/* Team 1 */}
                        <div 
                          className={`px-4 py-3 border-b border-zinc-800 flex items-center justify-between ${
                            match.winner_id === match.team1_id ? 'bg-cyan-500/10' : ''
                          } ${isAdmin && !match.team1_id && match.round === 1 ? 'cursor-pointer hover:bg-zinc-800/50' : ''}`}
                          onClick={() => {
                            if (isAdmin && !match.team1_id && match.round === 1) openAssignSlot(match.id, 'team1');
                          }}
                        >
                          <span className={`text-sm truncate ${
                            match.team1_name ? 'text-white' : isAdmin && match.round === 1 ? 'text-cyan-400/60 hover:text-cyan-400' : 'text-zinc-600'
                          }`}>
                            {match.team1_name || (isAdmin && match.round === 1 ? '+ Assign Team' : 'TBD')}
                          </span>
                          {match.team1_score !== null && (
                            <span className="text-sm font-mono text-cyan-400 ml-2">
                              {match.team1_score}
                            </span>
                          )}
                          {match.winner_id === match.team1_id && (
                            <Trophy className="w-4 h-4 text-yellow-500 ml-2" />
                          )}
                        </div>
                        
                        {/* Team 2 */}
                        <div 
                          className={`px-4 py-3 flex items-center justify-between ${
                            match.winner_id === match.team2_id ? 'bg-cyan-500/10' : ''
                          } ${isAdmin && !match.team2_id && match.round === 1 ? 'cursor-pointer hover:bg-zinc-800/50' : ''}`}
                          onClick={() => {
                            if (isAdmin && !match.team2_id && match.round === 1) openAssignSlot(match.id, 'team2');
                          }}
                        >
                          <span className={`text-sm truncate ${
                            match.team2_name ? 'text-white' : isAdmin && match.round === 1 ? 'text-cyan-400/60 hover:text-cyan-400' : 'text-zinc-600'
                          }`}>
                            {match.team2_name || (isAdmin && match.round === 1 ? '+ Assign Team' : 'TBD')}
                          </span>
                          {match.team2_score !== null && (
                            <span className="text-sm font-mono text-cyan-400 ml-2">
                              {match.team2_score}
                            </span>
                          )}
                          {match.winner_id === match.team2_id && (
                            <Trophy className="w-4 h-4 text-yellow-500 ml-2" />
                          )}
                        </div>

                        {/* Admin Edit Button */}
                        {isAdmin && match.team1_id && match.team2_id && (
                          <button
                            onClick={() => openEditDialog(match)}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-500 text-black flex items-center justify-center hover:bg-yellow-400 transition-colors"
                            title="Edit match"
                            data-testid={`edit-match-${match.id}`}
                          >
                            <Edit3 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Edit Match Dialog */}
        <Dialog open={!!editMatch} onOpenChange={() => setEditMatch(null)}>
          <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="edit-match-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase">
                Edit Match
              </DialogTitle>
            </DialogHeader>
            {editMatch && (
              <div className="space-y-6 mt-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm uppercase tracking-wide">
                    {editMatch.team1_name || 'Team 1'} Score
                  </Label>
                  <Input
                    type="number"
                    value={scores.team1}
                    onChange={(e) => setScores({...scores, team1: e.target.value})}
                    placeholder="0"
                    min="0"
                    className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400"
                    data-testid="team1-score-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm uppercase tracking-wide">
                    {editMatch.team2_name || 'Team 2'} Score
                  </Label>
                  <Input
                    type="number"
                    value={scores.team2}
                    onChange={(e) => setScores({...scores, team2: e.target.value})}
                    placeholder="0"
                    min="0"
                    className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400"
                    data-testid="team2-score-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm uppercase tracking-wide">
                    Winner
                  </Label>
                  <Select
                    value={scores.winner}
                    onValueChange={(val) => setScores({...scores, winner: val})}
                  >
                    <SelectTrigger className="bg-black border-zinc-800 text-white rounded-none h-12" data-testid="winner-select">
                      <SelectValue placeholder="Select winner" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-zinc-800 rounded-none">
                      {editMatch.team1_id && (
                        <SelectItem value={editMatch.team1_id} className="text-white">
                          {editMatch.team1_name}
                        </SelectItem>
                      )}
                      {editMatch.team2_id && (
                        <SelectItem value={editMatch.team2_id} className="text-white">
                          {editMatch.team2_name}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleUpdateMatch}
                  className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12"
                  data-testid="save-match-button"
                >
                  Save Changes
                </Button>

                {/* Reset & Remove Actions */}
                <div className="border-t border-zinc-800 pt-4 space-y-3">
                  {(editMatch.winner_id || editMatch.team1_score !== null) && (
                    <Button
                      onClick={() => handleResetMatch(editMatch.id)}
                      variant="outline"
                      className="w-full border-zinc-700 text-zinc-400 hover:text-white hover:border-white rounded-none h-10"
                      data-testid="reset-match-button"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Reset Winner & Scores
                    </Button>
                  )}
                  {editMatch.round === 1 && (
                    <div className="grid grid-cols-2 gap-3">
                      {editMatch.team1_id && (
                        <Button
                          onClick={() => handleRemoveTeam(editMatch.id, 'team1')}
                          variant="outline"
                          className="border-red-500/50 text-red-500 hover:bg-red-500/10 rounded-none h-10 text-xs"
                          data-testid="remove-team1-button"
                        >
                          <UserMinus className="w-3 h-3 mr-1" />
                          Remove {editMatch.team1_name}
                        </Button>
                      )}
                      {editMatch.team2_id && (
                        <Button
                          onClick={() => handleRemoveTeam(editMatch.id, 'team2')}
                          variant="outline"
                          className="border-red-500/50 text-red-500 hover:bg-red-500/10 rounded-none h-10 text-xs"
                          data-testid="remove-team2-button"
                        >
                          <UserMinus className="w-3 h-3 mr-1" />
                          Remove {editMatch.team2_name}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Legend */}
        {tournament && (
          <div className="mt-8 flex flex-wrap gap-6 text-sm text-zinc-500" data-testid="bracket-legend">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-cyan-500/10 border border-cyan-500/50" />
              <span>Completed Match</span>
            </div>
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-yellow-500" />
              <span>Winner</span>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 bg-yellow-500 flex items-center justify-center">
                  <Edit3 className="w-3 h-3 text-black" />
                </div>
                <span>Admin Edit</span>
              </div>
            )}
          </div>
        )}

        {/* Assign Team Dialog */}
        <Dialog open={!!assignSlot} onOpenChange={() => setAssignSlot(null)}>
          <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="assign-team-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase">
                Assign Team
              </DialogTitle>
            </DialogHeader>
            <p className="text-zinc-400 text-sm">
              Select a team to place in this bracket slot.
            </p>
            <div className="mt-4 space-y-2 max-h-[400px] overflow-y-auto">
              {availableTeams.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-zinc-500">No available teams to assign.</p>
                  <p className="text-zinc-600 text-sm mt-1">All teams are already in the bracket or no approved teams exist.</p>
                </div>
              ) : (
                availableTeams.map((team) => (
                  <button
                    key={team.id}
                    onClick={() => handleAssignTeam(team.id)}
                    className="w-full flex items-center gap-4 p-4 bg-black border border-zinc-800 hover:border-cyan-400/50 hover:bg-cyan-400/5 transition-all text-left"
                    data-testid={`assign-team-${team.id}`}
                  >
                    <div className="w-12 h-12 bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
                      <span className="font-mono text-sm text-cyan-400 font-bold">{team.tag}</span>
                    </div>
                    <div>
                      <h4 className="text-white font-bold">{team.name}</h4>
                      <p className="text-zinc-500 text-xs">Captain: {team.captain_name}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </main>
  );
}
